<template>
    <div class="head">
        <div class="title">{{ trans.receipt.payment }}</div>
        <GoBackButton @click.prevent="onGoBackButtonClick" />
        <LanguageSelector v-if="kiosk.isKioskMode" />
    </div>

    <div class="scrollable">
        <div v-if="isLoading" class="loading">
            <div class="loader"></div>
            {{ trans.receipt.loading }}
        </div>
        <template v-if="!isLoading && currentPayment != null">
            <div class="restaurant">{{ locationName }}</div>
            <div v-if="confirmDate" class="date">{{ confirmDate }}</div>

            <PaymentItemsList
                :paymentItems="currentPayment.items"
                :showInternalNames="false"
                :alwaysShowGroupName="true"
            />

            <div class="total">
                <div v-if="currentPayment.orderLevelDiscountAmount != 0" class="row">
                    <div class="lbl">{{ translations.pay.discount }}</div>
                    <div class="unit" style="color: #fd397a">{{ translations.currency.chf }}</div>
                    <div class="amt discount" style="color: #fd397a">
                        {{ toMoney(-1 * currentPayment.orderLevelDiscountAmount, true) }}
                    </div>
                </div>
                <div class="row">
                    <div class="lbl">{{ translations.pay.consumption }}</div>
                    <div class="unit">{{ translations.currency.chf }}</div>
                    <div class="amt">{{ toMoney(currentPayment.consumption, true) }}</div>
                </div>
                <div v-if="currentPayment.tip != null && currentPayment.tip !== 0" class="row">
                    <div class="lbl">{{ translations.pay.tip }}</div>
                    <div class="unit">{{ translations.currency.chf }}</div>
                    <div class="amt">{{ toMoney(currentPayment.tip, true) }}</div>
                </div>
                <div class="row total">
                    <div class="total-container" v-html="paymentMethodDivs"></div>
                    <div class="unit total">{{ translations.currency.chf }}</div>
                    <div class="amt total">{{ toMoney(currentPayment.totalPaid, true) }}</div>
                </div>
            </div>
        </template>
    </div>

    <div class="btn-container">
        <div ref="sendReceiptButtonElement" class="btn sendreceiptbtn" @click="sendReceipt">
            {{ trans.receipt.receipt }}
        </div>
    </div>
</template>

<script setup lang="ts">
    const kiosk = useKioskStore();
    import { PaymentReceiptController } from "@/app/controller/PaymentReceiptController";
    import { trans } from "@/app/localization/Trans";
    import { SendBillPaymentReceiptPopover } from "@/app/popover/SendBillPaymentReceiptPopover";
    import { SendOrderPaymentReceiptPopover } from "@/app/popover/SendOrderPaymentReceiptPopover";
    import { Popup } from "@/components/dialog/Popup";
    import type { ICustomerPayment } from "@/data/model/CustomerPayment";
    import { PaymentFor } from "@/data/model/Enums";
    import { DateTime } from "@/data/util/DateTime";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { Ripple } from "@/shared/ui/Ripple";
    import { PaymentMethodUtil } from "@/shared/utils/PaymentMethodUtil";
    import { toMoney } from "@/shared/utils/format";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { computed, onMounted, onUnmounted, ref } from "vue";

    import GoBackButton from "@/app/components/header/GoBackButton.vue";
    import PaymentItemsList from "@/components/order/PaymentItemsList.vue";
    import LanguageSelector from "@/app/components/language/LanguageSelector.vue";

    const props = defineProps<{
        data: ICustomerPayment;
    }>();

    function onGoBackButtonClick() {
        PaymentReceiptController.onGoBack.dispatch();
    }

    // state
    const currentPayment = ref<ICustomerPayment | null>(null);
    const isLoading = ref(false);

    // html refs
    const sendReceiptButtonElement = ref<HTMLDivElement | null>(null);

    // computed
    const locationName = computed(() => currentPayment.value?.locationName);
    const confirmDate = computed(() => {
        if (currentPayment.value == null) return null;

        const confirmDate = currentPayment.value.confirmDate ?? currentPayment.value.created;
        return `${DateTime.toDateString(confirmDate)}, ${DateTime.toTimeString(confirmDate)}`;
    });
    const paymentMethodDivs = computed(() => {
        const div = `<div class="lbl">${trans.receipt.total}</div>`;
        if (currentPayment.value == null) return div;

        return (
            div +
            PaymentMethodUtil.generatePaymentMethodSplitDivs("", currentPayment.value!.paymentMethodSplits).join(" ")
        );
    });

    // functions
    function sendReceipt() {
        switch (props.data.paymentFor) {
            case PaymentFor.Bill: {
                const billReceiptPopover = new SendBillPaymentReceiptPopover(currentPayment.value!.id);
                billReceiptPopover.show();
                break;
            }
            case PaymentFor.Order: {
                if (currentPayment.value?.transactionOrders.length !== 1) {
                    const errorMessage = `Order payment receipt for the payment ${currentPayment.value?.id} cannot be sent due to invalid number of orders`;
                    logError(errorMessage);
                    Popup.show({ message: errorMessage });
                    return;
                }
                const orderReceiptPopover = new SendOrderPaymentReceiptPopover(
                    currentPayment.value!.transactionOrders[0].orderId,
                    currentPayment.value!.locationId!
                );
                orderReceiptPopover.show();
                break;
            }
        }
    }

    onMounted(() => {
        Ripple.bind($(sendReceiptButtonElement.value!));

        currentPayment.value = props.data;
    });

    onUnmounted(() => PaymentReceiptController.onViewUnmounted.dispatch());
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .app > .payment-receipt {
        > .scrollable {
            position: absolute;
            top: 53px;
            left: 0;
            bottom: 0;
            right: 0;
            padding: 24px 24px 134px 24px;

            > .restaurant {
                font-size: 16px;
                line-height: 20px;
                color: colors.$fontColor;
                font-weight: 600;
                padding: 6px 0;
            }

            > .date {
                font-size: 14px;
                font-weight: 300;
                color: #aaaaaa;
                line-height: 16px;
                padding-bottom: 6px;
            }

            > .loading {
                margin-top: 60px;
                position: absolute;
                left: 50%;
                white-space: nowrap;
                transform: translateX(-50%);

                > .loader {
                    background-image: url("/assets/img/app/loading_green.svg");
                }
            }

            > .total {
                padding-top: 12px;

                > .row {
                    font-size: 14px;
                    color: colors.$fontColor;
                    display: flex;
                    flex-direction: row;
                    flex-wrap: nowrap;
                    justify-content: space-between;
                    line-height: 24px;

                    &.total {
                        padding-top: 8px;
                    }

                    > .lbl {
                        flex-grow: 1;
                        display: flex;
                        white-space: nowrap;
                    }

                    .total-container {
                        flex-grow: 1;
                        display: flex;

                        > .lbl {
                            white-space: nowrap;
                            font-weight: 600;
                        }

                        > .ccinfo {
                            line-height: 22px;
                            white-space: nowrap;
                            overflow: hidden;
                            font-size: 12px;

                            .brandicon {
                                display: inline-block;
                                vertical-align: middle;
                                width: 30px;
                                height: 22px;
                                margin-left: 8px;
                                background-size: contain;
                                background-repeat: no-repeat;
                                background-position: center center;
                            }
                        }
                    }

                    > .unit {
                        flex: 0 0 30px;
                        text-align: right;

                        &.total {
                            font-weight: 600;
                        }
                    }

                    > .amt {
                        flex: 0 0 50px;
                        text-align: right;
                        padding-left: 6px;
                        white-space: nowrap;

                        &.total {
                            font-weight: 600;
                        }
                    }
                }
            }
        }

        > .btn-container {
            padding: 14px 28px 14px 28px;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            flex-direction: column-reverse;

            > .sendreceiptbtn {
                background-color: var(--colorPrimary);
                color: #fff;
                border-radius: 23px;
                text-align: center;
                font-size: 17px;
                line-height: 46px;
                cursor: pointer;

                @include mixins.hover-and-active() {
                    background-color: var(--colorPrimaryHover);
                }
            }

            > .cancelbtn {
                background-color: colors.$red;
                color: #fff;
                margin-top: 14px;
                border-radius: 23px;
                text-align: center;
                font-size: 17px;
                line-height: 46px;
                cursor: pointer;

                @include mixins.hover-and-active() {
                    background-color: colors.$redHover;
                }
            }
        }
    }
</style>
